/**
 * JWT inspection helpers shared by web and admin.
 *
 * These only *read* tokens (format sanity check + expiry extraction) — no
 * signature verification happens client-side, that is the backend's job.
 */

/**
 * Validate JWT token format.
 * A valid JWT has 3 non-empty parts separated by dots: header.payload.signature
 * @param {string} token - Token to validate
 * @returns {boolean} True if token has valid JWT format
 */
export function isValidJWT(token) {
  if (!token || typeof token !== 'string') {
    return false;
  }

  const parts = token.split('.');

  if (parts.length !== 3) {
    return false;
  }

  if (parts.some(part => !part)) {
    return false;
  }

  // Try to decode the payload to ensure it's valid base64
  try {
    atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'));
    return true;
  } catch {
    return false;
  }
}

/**
 * Extract expiration timestamp from a JWT payload.
 * @param {string} token - JWT token
 * @returns {number|null} Expiration timestamp in milliseconds, or null if not found
 */
export function getJWTExpiration(token) {
  if (!isValidJWT(token)) {
    return null;
  }

  try {
    const parts = token.split('.');
    const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));

    // JWT exp is in seconds, convert to milliseconds
    if (payload.exp && typeof payload.exp === 'number') {
      return payload.exp * 1000;
    }

    return null;
  } catch (e) {
    console.warn('⚠️ Failed to extract expiration from JWT:', e.message);
    return null;
  }
}
